import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  BarChart3,
  Users,
  Eye,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
  Clock,
  Download,
  Filter,
  RefreshCw,
} from 'lucide-react';
import '../styles/analytics.css';

const AnalyticsPage = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, realtimeRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/summary?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/realtime`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (realtimeRes.ok) setRealtime(await realtimeRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtime = async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/realtime`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRealtime(await res.json());
    } catch (error) {
      console.error('Failed to fetch realtime:', error);
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  const exportData = () => {
    if (!analytics) return;
    const data = JSON.stringify(analytics, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${period}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="analytics-loading">
          <div className="loading-spinner" />
          <p>Loading analytics...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="analytics-page">
        {/* Header */}
        <div className="analytics-header">
          <div>
            <h2>Analytics</h2>
            <p>Detailed visitor statistics and insights</p>
          </div>
          <div className="header-actions">
            <div className="period-selector">
              {['7d', '30d', '90d', 'all'].map((p) => (
                <button
                  key={p}
                  className={period === p ? 'active' : ''}
                  onClick={() => setPeriod(p)}
                >
                  {p === '7d' ? '7D' : p === '30d' ? '30D' : p === '90d' ? '90D' : 'All'}
                </button>
              ))}
            </div>
            <button className="btn-secondary" onClick={fetchAnalytics}>
              <RefreshCw size={16} />
            </button>
            <button className="btn-secondary" onClick={exportData}>
              <Download size={16} /> Export
            </button>
          </div>
        </div>

        {/* Realtime Banner */}
        {realtime && (
          <div className="realtime-card">
            <div className="realtime-header">
              <span className="pulse" />
              <span>Real-time</span>
            </div>
            <div className="realtime-content">
              <div className="realtime-stat">
                <span className="stat-value">{realtime.active_visitors}</span>
                <span className="stat-label">Active Now</span>
              </div>
              <div className="realtime-stat">
                <span className="stat-value">{realtime.views_last_30min}</span>
                <span className="stat-label">Last 30 min</span>
              </div>
            </div>
          </div>
        )}

        {/* Overview Stats */}
        <div className="stats-row">
          <div className="stat-card">
            <Eye size={24} />
            <div>
              <span className="stat-value">
                {analytics?.visitor_stats?.total_visits?.toLocaleString() || 0}
              </span>
              <span className="stat-label">Total Views</span>
            </div>
          </div>
          <div className="stat-card">
            <Users size={24} />
            <div>
              <span className="stat-value">
                {analytics?.visitor_stats?.unique_visitors?.toLocaleString() || 0}
              </span>
              <span className="stat-label">Unique Visitors</span>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="analytics-grid">
          {/* Daily Chart */}
          {analytics?.daily_stats?.length > 0 && (
            <div className="analytics-card full-width">
              <div className="card-header">
                <h3><TrendingUp size={18} /> Daily Traffic</h3>
              </div>
              <div className="card-content">
                <div className="line-chart">
                  {analytics.daily_stats.map((day, idx) => {
                    const maxVisits = Math.max(...analytics.daily_stats.map(d => d.visits));
                    const height = maxVisits > 0 ? (day.visits / maxVisits) * 100 : 0;
                    return (
                      <div key={idx} className="chart-column">
                        <div className="chart-bar-wrapper">
                          <div 
                            className="chart-bar"
                            style={{ height: `${Math.max(5, height)}%` }}
                          >
                            <span className="bar-tooltip">{day.visits}</span>
                          </div>
                        </div>
                        <span className="chart-label">
                          {new Date(day.date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Top Pages */}
          <div className="analytics-card">
            <div className="card-header">
              <h3><BarChart3 size={18} /> Top Pages</h3>
            </div>
            <div className="card-content">
              {analytics?.top_pages?.length > 0 ? (
                <div className="list-table">
                  {analytics.top_pages.map((page, idx) => (
                    <div key={idx} className="list-row">
                      <span className="page-path">{page.page_path}</span>
                      <div className="page-stats">
                        <span className="views">{page.views} views</span>
                        <span className="visitors">{page.unique_visitors} visitors</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">No page data yet</p>
              )}
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="analytics-card">
            <div className="card-header">
              <h3><Globe size={18} /> Traffic Sources</h3>
            </div>
            <div className="card-content">
              {analytics?.top_referrers?.length > 0 ? (
                <div className="list-table">
                  {analytics.top_referrers.map((ref, idx) => (
                    <div key={idx} className="list-row">
                      <span className="referrer">{ref.referrer}</span>
                      <div className="referrer-stats">
                        <span className="visits">{ref.visits}</span>
                        <span className="percent">{ref.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">No referrer data yet</p>
              )}
            </div>
          </div>

          {/* Devices */}
          <div className="analytics-card">
            <div className="card-header">
              <h3><Monitor size={18} /> Devices</h3>
            </div>
            <div className="card-content">
              {analytics?.devices?.length > 0 ? (
                <div className="device-breakdown">
                  {analytics.devices.map((device, idx) => {
                    const DeviceIcon = getDeviceIcon(device.device_type);
                    return (
                      <div key={idx} className="device-row">
                        <div className="device-info">
                          <DeviceIcon size={20} />
                          <span>{device.device_type}</span>
                        </div>
                        <div className="device-bar">
                          <div 
                            className="bar-fill" 
                            style={{ width: `${device.percentage}%` }}
                          />
                        </div>
                        <span className="percent">{device.percentage}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-text">No device data yet</p>
              )}
            </div>
          </div>

          {/* Countries */}
          <div className="analytics-card">
            <div className="card-header">
              <h3><Globe size={18} /> Top Countries</h3>
            </div>
            <div className="card-content">
              {analytics?.countries?.length > 0 ? (
                <div className="list-table">
                  {analytics.countries.map((country, idx) => (
                    <div key={idx} className="list-row">
                      <span className="country">{country.country}</span>
                      <div className="country-stats">
                        <span className="visits">{country.visits}</span>
                        <span className="percent">{country.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">No country data yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;
